import { useEffect, useRef, type CSSProperties } from 'react'
import clsx from 'clsx'

import type { SearchDomain } from '../../lib/data'
import { SEARCH_DOMAINS } from '../../lib/data'
import { DOMAIN_LABEL } from './labels'
import { Cross, Magnifier } from './icons'

// ============================================================================
// SEARCH · THE HEADER — docs/design/handoff/"LifeOS Search.dc.html" (10a/10b).
//
// This screen has NO domain band. It belongs to all five domains at once, so a
// hue pair would be a claim about which one; the mockup draws a neutral vertical
// fade instead (`--band-search`, index.css). The field sits ON that fade as a
// white card with its own lift — it is the screen, not a control on a screen.
// ============================================================================

export function SearchField({
  value,
  onChange,
  onClear,
  countLine,
  autoFocus,
}: {
  value: string
  onChange: (next: string) => void
  onClear: () => void
  /** '14 results' / '2 exact · 5 related'. Null while there is nothing true to count. */
  countLine: string | null
  autoFocus?: boolean
}) {
  const ref = useRef<HTMLInputElement>(null)

  // The screen exists to be typed into. Arriving from the header field with a query already in
  // the URL should still land the caret here, so the next keystroke edits the query instead of
  // going nowhere — and `autoFocus` as an attribute is unreliable inside a router transition.
  useEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus])

  return (
    // 12d, TEXT ENTRY (motion-brief §2). The search field is the fully-rounded shape, so hover
    // FILLS it from the left with the neutral `field-hover`, and focus CLEARS that fill and hands
    // the edge to the ring — hover and focus must never read as the same state, which is exactly
    // what the old `focus-within:border-ink` did once anything else on the row also darkened.
    <div
      style={{ '--mo-rest-shadow': 'var(--shadow-search)' } as CSSProperties}
      className="mo-field flex flex-none items-center gap-[14px] rounded-field border border-line-2 bg-surface px-5 py-[15px] shadow-search"
    >
      <Magnifier size={21} strokeWidth={1.8} className="flex-none text-ink-3" />
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // esc clears without leaving the screen — the field is the screen, so closing it would
          // mean going somewhere, and there is nowhere to go back to that is more useful.
          if (e.key === 'Escape' && value) {
            e.preventDefault()
            onClear()
          }
        }}
        placeholder="Search everything"
        aria-label="Search everything"
        // `[&::-webkit-search-cancel-button]:hidden` — type=search gives semantics and the
        // browser's own clear affordance; the mockup draws its own, and two of them side by side
        // is a control the owner has to choose between for no reason.
        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[22px] font-normal leading-[1.2] tracking-[-0.01em] text-ink outline-none placeholder:text-faintest-2 [&::-webkit-search-cancel-button]:hidden"
      />
      {countLine && (
        <span className="flex-none text-[11.5px] font-normal leading-none text-microcopy">
          {countLine}
        </span>
      )}
      {value.length > 0 && (
        <>
          <span className="h-[18px] w-px flex-none bg-line" aria-hidden />
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear the search"
            className="flex-none rounded-chip text-microcopy transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-ink-2"
          >
            <Cross />
          </button>
        </>
      )}
    </div>
  )
}

/**
 * The domain chips, with REAL counts and REAL filtering — they narrow the literal block to one
 * domain and the selection lives in the URL (`?in=`) beside the query.
 *
 * A domain with no matches is rendered and disabled rather than hidden. Hiding it would make the
 * row's width change under the owner's hand as he types, and "School 0" is information: it says
 * the app looked there.
 */
export function DomainChips({
  counts,
  total,
  active,
  onPick,
}: {
  counts: Record<SearchDomain, number>
  total: number
  active: SearchDomain | null
  onPick: (next: SearchDomain | null) => void
}) {
  return (
    <>
      <button
        type="button"
        onClick={() => onPick(null)}
        aria-pressed={active === null}
        className={clsx(
          'flex-none rounded-pill px-[13px] py-[6px] text-[11.5px] leading-none transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
          active === null
            ? 'bg-ink font-semibold text-surface'
            : 'border border-line bg-surface font-medium text-ink-2 hover:bg-surface-2',
        )}
      >
        All
        <span className={clsx('font-medium', active === null ? 'text-muted' : 'text-microcopy')}>
          {' '}
          {total}
        </span>
      </button>

      {SEARCH_DOMAINS.map((domain) => {
        const count = counts[domain]
        const on = active === domain
        return (
          <button
            key={domain}
            type="button"
            disabled={count === 0 && !on}
            aria-pressed={on}
            onClick={() => onPick(on ? null : domain)}
            title={count === 0 ? `Nothing in ${DOMAIN_LABEL[domain]} contains those words.` : undefined}
            className={clsx(
              'flex-none rounded-pill bg-surface px-[13px] py-[6px] text-[11.5px] leading-none transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              on
                ? 'border-[1.5px] border-ink font-semibold text-ink'
                : count === 0
                  ? 'cursor-not-allowed border border-line font-medium text-microcopy'
                  : 'border border-line font-medium text-ink-2 hover:bg-surface-2',
            )}
          >
            {DOMAIN_LABEL[domain]}
            <span className={clsx('font-medium', count === 0 ? '' : 'text-microcopy')}> {count}</span>
          </button>
        )
      })}
    </>
  )
}

/**
 * The date-scope chip. It is INERT and says so on hover, rather than silently doing nothing.
 *
 * The dates to filter on exist in this dataset, so this is not a data gap — it is a scope call:
 * a time filter is a second axis on a screen whose first axis (domain) is already live, and the
 * smaller version ships first. It renders because the mockup draws it and a dropped control is a
 * dropped state (docs/design/FIDELITY.md rule 3).
 */
export function DateScopeChip() {
  return (
    <button
      type="button"
      aria-disabled="true"
      onClick={(e) => e.preventDefault()}
      title="Narrowing by date is not built yet. The domain chips beside this one are live, and they filter for real."
      className="flex flex-none cursor-not-allowed items-center gap-[7px] rounded-pill border border-line bg-surface px-[13px] py-[6px] text-[11.5px] font-medium leading-none text-ink-2"
    >
      <svg
        width={12}
        height={12}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="flex-none text-muted"
        aria-hidden="true"
      >
        <path d="M4 7.4A2.4 2.4 0 0 1 6.4 5h11.2A2.4 2.4 0 0 1 20 7.4v11.2A2.4 2.4 0 0 1 17.6 21H6.4A2.4 2.4 0 0 1 4 18.6zM4 10h16" />
      </svg>
      Any time
    </button>
  )
}

/**
 * `recent: extended mind · sublet · factoring`.
 *
 * SESSION-SCOPED AND SAID SO BY BEHAVING THAT WAY: it is this tab's history, it is not written
 * anywhere, and it is gone on reload. Persisting it would mean storing a list of the owner's
 * questions, which is a decision about his data, not a decision about a strip of 11px grey text.
 */
export function RecentStrip({ items, onPick }: { items: readonly string[]; onPick: (q: string) => void }) {
  if (items.length === 0) return null
  return (
    <div className="flex flex-none items-center gap-2">
      <span className="text-[10.5px] font-normal leading-none text-faintest-2">recent:</span>
      {items.map((q, i) => (
        <span key={q} className="flex items-center gap-2">
          {i > 0 && <span className="size-[3px] rounded-full bg-dash" aria-hidden />}
          <button
            type="button"
            onClick={() => onPick(q)}
            className="max-w-[160px] truncate rounded-chip text-[11px] font-medium leading-none text-muted transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-ink-2"
          >
            {q}
          </button>
        </span>
      ))}
    </div>
  )
}
